export interface CSVStructure {
  hasLocationColumn: boolean;
  hasYearColumn: boolean;
  hasMonthColumn: boolean;
  isMultiPeriod: boolean;
  isMultiLocation: boolean;
  locationColumnIndex: number;
  yearColumnIndex: number;
  monthColumnIndex: number;
  kpiColumns: string[];
  kpiColumnIndices: number[];
  confidence: {
    location: number;
    year: number;
    month: number;
  };
}

export interface CSVValidationResult {
  isValid: boolean;
  structure: CSVStructure;
  errors: string[];
  warnings: string[];
  recommendations: string[];
}

/**
 * Detect CSV structure and identify column types
 */
export const detectCSVStructure = (csvData: string[][]): CSVStructure => {
  if (!csvData || csvData.length === 0) {
    throw new Error('CSV data is empty');
  }

  const headers = csvData[0];
  const dataRows = csvData.slice(1);

  // Find location column with confidence scoring
  const locationResult = findLocationColumn(headers, dataRows);
  
  // Find year column with confidence scoring
  const yearResult = findYearColumn(headers, dataRows);
  
  // Find month column with confidence scoring
  const monthResult = findMonthColumn(headers, dataRows);

  // Identify KPI columns (everything else)
  const kpiColumnIndices = headers
    .map((_, index) => index)
    .filter(index => 
      index !== locationResult.index && 
      index !== yearResult.index && 
      index !== monthResult.index
    );

  const kpiColumns = kpiColumnIndices.map(index => headers[index]);

  return {
    hasLocationColumn: locationResult.found,
    hasYearColumn: yearResult.found,
    hasMonthColumn: monthResult.found,
    isMultiPeriod: yearResult.found && monthResult.found,
    isMultiLocation: locationResult.found,
    locationColumnIndex: locationResult.index,
    yearColumnIndex: yearResult.index,
    monthColumnIndex: monthResult.index,
    kpiColumns,
    kpiColumnIndices,
    confidence: {
      location: locationResult.confidence,
      year: yearResult.confidence,
      month: monthResult.confidence
    }
  };
};

/**
 * Find location/dealer column
 */
const findLocationColumn = (headers: string[], dataRows: string[][]): {
  found: boolean;
  index: number;
  confidence: number;
} => {
  let bestMatch = { index: -1, confidence: 0 };

  headers.forEach((header, index) => {
    const headerLower = header.toLowerCase().trim();
    let confidence = 0;

    // Direct keyword matches
    if (headerLower === 'location') confidence = 100;
    else if (headerLower === 'dealer') confidence = 95;
    else if (headerLower === 'dealership') confidence = 90;
    else if (headerLower === 'store') confidence = 85;
    else if (headerLower === 'branch') confidence = 80;
    else if (headerLower === 'site') confidence = 75;
    else if (headerLower.includes('location')) confidence = 70;
    else if (headerLower.includes('dealer')) confidence = 65;
    else if (headerLower.includes('store')) confidence = 60;
    else if (headerLower.includes('branch')) confidence = 55;

    // Analyze data content if we have sample rows
    if (confidence > 0 && dataRows.length > 0) {
      const sampleValues = dataRows.slice(0, Math.min(5, dataRows.length)).map(row => row[index]);
      const hasLocationLikeValues = sampleValues.some(value => 
        value && typeof value === 'string' && value.length > 3 && 
        (value.includes('Location') || value.includes('Store') || value.includes('Branch'))
      );
      
      if (hasLocationLikeValues) {
        confidence += 10;
      }
    }

    if (confidence > bestMatch.confidence) {
      bestMatch = { index, confidence };
    }
  });

  return {
    found: bestMatch.confidence >= 50,
    index: bestMatch.index,
    confidence: bestMatch.confidence
  };
};

/**
 * Find year column
 */
const findYearColumn = (headers: string[], dataRows: string[][]): {
  found: boolean;
  index: number;
  confidence: number;
} => {
  let bestMatch = { index: -1, confidence: 0 };

  headers.forEach((header, index) => {
    const headerLower = header.toLowerCase().trim();
    let confidence = 0;

    // Direct keyword matches
    if (headerLower === 'year') confidence = 100;
    else if (headerLower === 'yr') confidence = 90;
    else if (headerLower.includes('year')) confidence = 80;
    else if (headerLower.match(/^\d{4}$/)) confidence = 70; // Header is a 4-digit year

    // Analyze data content
    if (dataRows.length > 0) {
      const sampleValues = dataRows.slice(0, Math.min(10, dataRows.length)).map(row => row[index]);
      const yearValues = sampleValues.filter(value => {
        const num = parseInt(value);
        return !isNaN(num) && num >= 1900 && num <= 3000 && value.length === 4;
      });

      const yearPercentage = (yearValues.length / sampleValues.length) * 100;
      
      if (yearPercentage >= 80) confidence += 50;
      else if (yearPercentage >= 50) confidence += 30;
      else if (yearPercentage >= 25) confidence += 15;
    }

    if (confidence > bestMatch.confidence) {
      bestMatch = { index, confidence };
    }
  });

  return {
    found: bestMatch.confidence >= 50,
    index: bestMatch.index,
    confidence: bestMatch.confidence
  };
};

/**
 * Find month column
 */
const findMonthColumn = (headers: string[], dataRows: string[][]): {
  found: boolean;
  index: number;
  confidence: number;
} => {
  let bestMatch = { index: -1, confidence: 0 };

  headers.forEach((header, index) => {
    const headerLower = header.toLowerCase().trim();
    let confidence = 0;

    // Direct keyword matches
    if (headerLower === 'month') confidence = 100;
    else if (headerLower === 'mon') confidence = 90;
    else if (headerLower === 'mth') confidence = 85;
    else if (headerLower.includes('month')) confidence = 80;

    // Analyze data content
    if (dataRows.length > 0) {
      const sampleValues = dataRows.slice(0, Math.min(10, dataRows.length)).map(row => row[index]);
      
      // Check for numeric months (1-12)
      const numericMonths = sampleValues.filter(value => {
        const num = parseInt(value);
        return !isNaN(num) && num >= 1 && num <= 12;
      });

      // Check for month names
      const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 
                         'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
      const namedMonths = sampleValues.filter(value => 
        value && monthNames.some(month => 
          value.toLowerCase().includes(month)
        )
      );

      const numericPercentage = (numericMonths.length / sampleValues.length) * 100;
      const namedPercentage = (namedMonths.length / sampleValues.length) * 100;
      const totalPercentage = Math.max(numericPercentage, namedPercentage);
      
      if (totalPercentage >= 80) confidence += 50;
      else if (totalPercentage >= 50) confidence += 30;
      else if (totalPercentage >= 25) confidence += 15;
    }

    if (confidence > bestMatch.confidence) {
      bestMatch = { index, confidence };
    }
  });

  return {
    found: bestMatch.confidence >= 50,
    index: bestMatch.index,
    confidence: bestMatch.confidence
  };
};

/**
 * Validate CSV structure for different submission modes
 */
export const validateCSVForSubmissionMode = (
  csvData: string[][],
  isMultiLocationMode: boolean,
  isMultiPeriodMode: boolean
): CSVValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const recommendations: string[] = [];

  try {
    const structure = detectCSVStructure(csvData);

    // Validate multi-location requirements
    if (isMultiLocationMode && !structure.hasLocationColumn) {
      errors.push('Multi-location mode requires a "Location" or "Dealer" column');
      recommendations.push('Add a column named "Location", "Dealer", or "Dealership" with location names');
    }

    // Validate multi-period requirements
    if (isMultiPeriodMode) {
      if (!structure.hasYearColumn) {
        errors.push('Multi-period mode requires a "Year" column');
        recommendations.push('Add a "Year" column with 4-digit years (e.g., 2023)');
      }
      
      if (!structure.hasMonthColumn) {
        errors.push('Multi-period mode requires a "Month" column');
        recommendations.push('Add a "Month" column with month numbers (1-12) or month names');
      }
    }

    // Validate KPI columns
    if (structure.kpiColumns.length === 0) {
      errors.push('No KPI data columns found');
      recommendations.push('Include columns with KPI codes or names containing numeric data');
    }

    // Confidence warnings
    if (isMultiLocationMode && structure.hasLocationColumn && structure.confidence.location < 80) {
      warnings.push(`Location column detection has low confidence (${structure.confidence.location}%)`);
    }

    if (isMultiPeriodMode) {
      if (structure.hasYearColumn && structure.confidence.year < 80) {
        warnings.push(`Year column detection has low confidence (${structure.confidence.year}%)`);
      }
      
      if (structure.hasMonthColumn && structure.confidence.month < 80) {
        warnings.push(`Month column detection has low confidence (${structure.confidence.month}%)`);
      }
    }

    // Data validation for multi-period
    if (isMultiPeriodMode && structure.isMultiPeriod) {
      const dataRows = csvData.slice(1);
      const invalidRows: number[] = [];

      dataRows.forEach((row, rowIndex) => {
        if (structure.hasYearColumn) {
          const yearValue = row[structure.yearColumnIndex];
          const year = parseInt(yearValue);
          if (isNaN(year) || year < 1900 || year > 3000) {
            invalidRows.push(rowIndex + 2); // +2 for 1-based indexing and header row
          }
        }

        if (structure.hasMonthColumn) {
          const monthValue = row[structure.monthColumnIndex];
          const month = parseInt(monthValue);
          if (isNaN(month) || month < 1 || month > 12) {
            invalidRows.push(rowIndex + 2);
          }
        }
      });

      if (invalidRows.length > 0) {
        const uniqueRows = invalidRows.filter((value, index, self) => self.indexOf(value) === index);
        if (uniqueRows.length <= 5) {
          errors.push(`Invalid date values in rows: ${uniqueRows.join(', ')}`);
        } else {
          errors.push(`Invalid date values in ${uniqueRows.length} rows (first 5: ${uniqueRows.slice(0, 5).join(', ')})`);
        }
      }
    }

    return {
      isValid: errors.length === 0,
      structure,
      errors,
      warnings,
      recommendations
    };

  } catch (error) {
    return {
      isValid: false,
      structure: {
        hasLocationColumn: false,
        hasYearColumn: false,
        hasMonthColumn: false,
        isMultiPeriod: false,
        isMultiLocation: false,
        locationColumnIndex: -1,
        yearColumnIndex: -1,
        monthColumnIndex: -1,
        kpiColumns: [],
        kpiColumnIndices: [],
        confidence: { location: 0, year: 0, month: 0 }
      },
      errors: [`CSV structure detection failed: ${error instanceof Error ? error.message : 'Unknown error'}`],
      warnings: [],
      recommendations: ['Please check your CSV file format and try again']
    };
  }
};

/**
 * Generate helpful suggestions based on CSV structure
 */
export const generateCSVSuggestions = (structure: CSVStructure): string[] => {
  const suggestions: string[] = [];

  if (!structure.hasLocationColumn) {
    suggestions.push('Consider adding a "Location" column if you plan to use multi-location mode');
  }

  if (!structure.isMultiPeriod) {
    suggestions.push('To enable multi-period import, add "Year" and "Month" columns to your CSV');
  }

  if (structure.kpiColumns.length < 3) {
    suggestions.push('Add more KPI columns to maximize the value of your data import');
  }

  if (structure.confidence.location < 70 && structure.hasLocationColumn) {
    suggestions.push('Rename your location column to "Location" or "Dealer" for better detection');
  }

  return suggestions;
};
